import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import type { Review } from '../types/chat';

/**
 * Get review for a specific lesson
 */
export async function getReviewByLessonId(lessonId: string): Promise<Review | null> {
  const reviewQuery = query(
    collection(db, 'reviews'),
    where('lessonId', '==', lessonId)
  );

  const reviewSnap = await getDocs(reviewQuery);
  if (reviewSnap.empty) {
    return null;
  }

  const doc = reviewSnap.docs[0];
  const data = doc.data();
  return {
    ...data,
    createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
  } as Review;
}

/**
 * Check whether any review exists for a given order — used for training programs
 * where one review per program is the limit regardless of which lesson is being viewed.
 */
export async function getReviewByOrderId(orderId: string): Promise<Review | null> {
  const reviewQuery = query(
    collection(db, 'reviews'),
    where('orderId', '==', orderId)
  );
  const snap = await getDocs(reviewQuery);
  if (snap.empty) return null;
  const data = snap.docs[0].data();
  return {
    ...data,
    createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
  } as Review;
}

/**
 * Get reviews for a coach
 */
function mapReviewDocs(snap: import('firebase/firestore').QuerySnapshot): Review[] {
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
    } as Review;
  });
}

export async function getCoachReviews(coachId: string): Promise<Review[]> {
  // Ordered query requires composite index (coachId ASC, createdAt DESC).
  // If the index is not yet deployed, fall back to an unordered equality-only query
  // so the reviews section still renders rather than showing empty.
  try {
    const snap = await getDocs(query(
      collection(db, 'reviews'),
      where('coachId', '==', coachId),
      orderBy('createdAt', 'desc')
    ));
    return mapReviewDocs(snap);
  } catch {
    const snap = await getDocs(query(
      collection(db, 'reviews'),
      where('coachId', '==', coachId)
    ));
    return mapReviewDocs(snap).sort((a, b) =>
      new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );
  }
}

